import { useEffect, useRef, useState } from "react";

import {
  getChannelStreams,
  getChannelStreamPlay,
} from "../services/channelStreamsService";

const DEFAULT_LIMIT = 12;

const getMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.streams)) return data.streams;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const getId = (stream) =>
  String(stream?.streamSessionId ?? stream?.stream_session_id ?? "");

export const useChannelStreams = (username, limit = DEFAULT_LIMIT) => {
  const pageLimit = Math.max(1, Number(limit) || DEFAULT_LIMIT);

  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");

  const [selectedStream, setSelectedStream] = useState(null);
  const [watchData, setWatchData] = useState(null);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState("");

  const listRequestRef = useRef(0);
  const playbackRequestRef = useRef(0);
  const busyRef = useRef(false);
  const playbackControllerRef = useRef(null);
  const scopeRef = useRef({ username, pageLimit });

  scopeRef.current = { username, pageLimit };

  const fetchStreams = async ({ append = false, refresh = false } = {}) => {
    if (!username || (busyRef.current && (append || refresh))) {
      return;
    }

    const requestId = ++listRequestRef.current;
    const requestUsername = username;

    busyRef.current = true;

    if (append) {
      setLoadingMore(true);
    } else if (refresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setLoaded(false);
    }

    setError("");

    try {
      const offset = append ? streams.length : 0;

      const response = await getChannelStreams(requestUsername, {
        limit: pageLimit,
        offset,
      });

      if (
        requestId !== listRequestRef.current ||
        scopeRef.current.username !== requestUsername ||
        scopeRef.current.pageLimit !== pageLimit
      ) {
        return;
      }

      const data = response?.data ?? {};
      const items = normalizeList(data);
      const total = Number(data.total);

      if (append) {
        setStreams((previous) => {
          const existing = new Set(previous.map(getId));

          return [
            ...previous,
            ...items.filter((item) => !existing.has(getId(item))),
          ];
        });
      } else {
        setStreams(items);
      }

      setHasMore(
        typeof data.hasMore === "boolean"
          ? data.hasMore
          : Number.isFinite(total)
            ? offset + items.length < total
            : items.length === pageLimit,
      );
    } catch (requestError) {
      if (requestId !== listRequestRef.current) return;

      setError(getMessage(requestError, "Unable to load channel streams."));
    } finally {
      if (requestId === listRequestRef.current) {
        busyRef.current = false;
        setLoading(false);
        setLoaded(true);
        setLoadingMore(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    listRequestRef.current += 1;
    playbackRequestRef.current += 1;

    playbackControllerRef.current?.abort();
    playbackControllerRef.current = null;
    busyRef.current = false;

    setStreams([]);
    setLoading(true);
    setLoaded(false);
    setLoadingMore(false);
    setRefreshing(false);
    setHasMore(false);
    setError("");

    setSelectedStream(null);
    setWatchData(null);
    setWatchLoading(false);
    setWatchError("");

    if (!username) {
      setLoading(false);
      setLoaded(true);
      return;
    }

    void fetchStreams();

    return () => {
      listRequestRef.current += 1;
      playbackRequestRef.current += 1;
      playbackControllerRef.current?.abort();
      busyRef.current = false;
    };
  }, [username, pageLimit]);

  const refresh = async () => {
    if (busyRef.current) return;

    await fetchStreams({ refresh: true });
  };

  const loadMore = async () => {
    if (busyRef.current || !hasMore) return;

    await fetchStreams({ append: true });
  };

  const closeStream = () => {
    playbackRequestRef.current += 1;
    playbackControllerRef.current?.abort();
    playbackControllerRef.current = null;

    setSelectedStream(null);
    setWatchData(null);
    setWatchLoading(false);
    setWatchError("");
  };

  const openStream = async (stream) => {
    const streamSessionId = getId(stream);

    if (!username || !streamSessionId) return;

    const requestId = ++playbackRequestRef.current;
    const requestUsername = username;

    playbackControllerRef.current?.abort();

    const controller = new AbortController();
    playbackControllerRef.current = controller;

    setSelectedStream(stream);
    setWatchData(null);
    setWatchLoading(true);
    setWatchError("");

    try {
      const response = await getChannelStreamPlay(
        username,
        streamSessionId,
        controller.signal,
      );

      if (
        controller.signal.aborted ||
        requestId !== playbackRequestRef.current ||
        scopeRef.current.username !== requestUsername
      ) {
        return;
      }

      const data = response?.data ?? {};

      setWatchData(data.stream || data.playback || data);
    } catch (requestError) {
      if (
        controller.signal.aborted ||
        requestId !== playbackRequestRef.current
      ) {
        return;
      }

      setWatchError(getMessage(requestError, "Unable to open channel stream."));
    } finally {
      if (requestId === playbackRequestRef.current) {
        setWatchLoading(false);
      }
    }
  };

  return {
    streams,
    loading,
    loaded,
    loadingMore,
    refreshing,
    hasMore,
    error,
    refresh,
    loadMore,
    selectedStream,
    watchData,
    watchLoading,
    watchError,
    openStream,
    closeStream,
  };
};
